import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(ChartDataLabels);

const horizontalLinePlugin = {
  id: "horizontalLine",
  afterDraw(chart, args, options) {
    const {
      ctx,
      chartArea: { left, right },
      scales: { y }
    } = chart;
    if (!options.value) return;

    const yPos = y.getPixelForValue(options.value);

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left, yPos);
    ctx.lineTo(right, yPos);
    ctx.strokeStyle = "rgba(200,0,0,0.8)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }
};

Chart.register(horizontalLinePlugin);

// Connects to data-controller="chart"
export default class extends Controller {
  static targets = [
    "yearlyWeekdayWeekendAverageCanvas",
    "yearlyMonthlyAverageSeries",
    "daylyTransitionCanvas",
    "dayOfWeekCanvas",
    "timeCanvas",
    "dailyPeopleCanvas",
    "timePeopleCanvas",
    "monthlyWeekdayWeekendHolidayAverageCanvas",
    "taxiWaitingZeroPointsCanvas",
    "hourlyAverageCanvas"
  ];

  connect() {
    console.log("Chart controller connected");
    const url = this.data.get("url");
    this.renderChart(url);
  }

  async renderChart(url) {
    const response = await fetch(url, {
      headers: { Accept: "application/json" }
    });
    const data = await response.json();

    const datalabelsOptions = {
      anchor: "end",
      align: "end",
      color: "#333",
      font: { weight: "bold", size: 10 },
      formatter: (value) => {
        return value.toLocaleString();
      }
    };

    // 総数
    const totalCountElement = document.querySelector(
      "h5.my-auto span.text-danger"
    );
    if (totalCountElement) {
      totalCountElement.textContent = data.total_count.toLocaleString();
    }

    // 年別 平日・週末平均グラフ
    if (this.hasYearlyWeekdayWeekendAverageCanvasTarget) {
      if (this.yearlyWeekdayWeekendAverageChart)
        this.yearlyWeekdayWeekendAverageChart.destroy();
      this.yearlyWeekdayWeekendAverageChart = new Chart(
        this.yearlyWeekdayWeekendAverageCanvasTarget,
        {
          type: "bar",
          data: data.yearly_weekday_weekend_average_series,
          options: {
            plugins: {
              legend: { display: false },
              title: { display: true, text: "年・月別" },
              datalabels: datalabelsOptions
            }
          }
        }
      );
    }

    // 年間平均駐車台数推移グラフ
    if (this.hasYearlyMonthlyAverageSeriesTarget) {
      if (this.yearlyMonthlyAverageSeriesChart)
        this.yearlyMonthlyAverageSeriesChart.destroy();
      this.yearlyMonthlyAverageSeriesChart = new Chart(
        this.yearlyMonthlyAverageSeriesTarget,
        {
          type: "bar",
          data: data.yearly_monthly_average_series,
          options: {
            plugins: {
              legend: { position: "right" },
              title: { display: true, text: "年間平均駐車台数推移" },
              datalabels: datalabelsOptions
            },
            responsive: true,
            scales: {
              x: { title: { display: true, text: "月" } },
              y: { title: { display: true, text: "平均駐車台数" } }
            }
          }
        }
      );
    }

    if (this.hasDaylyTransitionCanvasTarget) {
      if (this.daylyTransitionChart) this.daylyTransitionChart.destroy();
      this.daylyTransitionChart = new Chart(this.daylyTransitionCanvasTarget, {
        type: "line",
        data: data.daily_transition_series,
        options: {
          plugins: {
            legend: { position: "right" },
            title: { display: true, text: "日別推移：2年比較" },
            datalabels: datalabelsOptions
          },
          responsive: true,
          scales: {
            x: { title: { display: true, text: "日付" } },
            y: { title: { display: true, text: "1日あたり台数" } }
          }
        }
      });
    }

    // 曜日別グラフ
    if (this.hasDayOfWeekCanvasTarget) {
      if (this.dayOfWeekChart) this.dayOfWeekChart.destroy();
      this.dayOfWeekChart = new Chart(this.dayOfWeekCanvasTarget, {
        type: "bar",
        data: data.day_of_week_series,
        options: {
          plugins: {
            legend: { display: false },
            // title: { display: true, text: "曜日別" }
            datalabels: datalabelsOptions
          }
        }
      });
    }

    // 時間帯別グラフ
    if (this.hasTimeCanvasTarget) {
      if (this.timeChart) this.timeChart.destroy();
      this.timeChart = new Chart(this.timeCanvasTarget, {
        type: "line",
        data: data.time_series,
        options: {
          plugins: {
            legend: { display: false },
            datalabels: {
              formatter: function (value) {
                return value + "台";
              }
            },
            datalabels: datalabelsOptions
            // title: { display: true, text: "時間帯別 人・車両" }
          }
        }
      });
    }

    if (this.hasDailyPeopleCanvasTarget) {
      if (this.dailyPeopleChart) this.dailyPeopleChart.destroy();
      this.dailyPeopleChart = new Chart(this.dailyPeopleCanvasTarget, {
        type: "line",
        data: data.daily_people_series,
        options: {
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: "日別通行人数（人）"
            },
            datalabels: datalabelsOptions,
            legend: {
              display: true,
              position: "top"
            }
          },
          scales: {
            x: {
              title: { display: true, text: "日付" }
            },
            y: {
              title: { display: true, text: "通行人数（人）" },
              beginAtZero: true
            }
          }
        }
      });
    }
    if (this.hasTimePeopleCanvasTarget) {
      if (this.timePeopleChart) this.timePeopleChart.destroy();
      this.timePeopleChart = new Chart(this.timePeopleCanvasTarget, {
        type: "line",
        data: data.time_people_series,
        options: {
          plugins: {
            legend: { display: false },
            datalabels: datalabelsOptions
            // title: { display: true, text: "時間帯別 人・車両" }
          }
        }
      });
    }

    if (this.hasHourlyAverageCanvasTarget) {
      if (this.hourlyAverageChart) this.hourlyAverageChart.destroy();
      this.hourlyAverageChart = new Chart(this.hourlyAverageCanvasTarget, {
        type: "line",
        data: data.hourly_average_series,
        options: {
          plugins: {
            legend: { position: "right" },
            title: { display: true, text: "時間帯別平均駐車台数（年比較）" },
            datalabels: datalabelsOptions,
            horizontalLine: { value: data.hourly_average_series.threshold }
          },
          scales: {
            x: { title: { display: true, text: "時間帯" } },
            y: { title: { display: true, text: "平均駐車台数" } }
          },
          tension: 0.1
        }
      });
    }

    if (this.hasMonthlyWeekdayWeekendHolidayAverageCanvasTarget) {
      if (this.monthlyWeekdayWeekendHolidayAverageChart)
        this.monthlyWeekdayWeekendHolidayAverageChart.destroy();
      this.monthlyWeekdayWeekendHolidayAverageChart = new Chart(
        this.monthlyWeekdayWeekendHolidayAverageCanvasTarget,
        {
          type: "bar",
          data: data.monthly_weekday_weekend_holiday_average_series,
          options: {
            plugins: {
              legend: { display: true },
              datalabels: datalabelsOptions
              // title: { display: true, text: "月別 平日・週末平均" }
            }
          }
        }
      );
    }
    if (this.hasTaxiWaitingZeroPointsCanvasTarget) {
      if (this.taxiWaitingZeroPointsChart)
        this.taxiWaitingZeroPointsChart.destroy();
      this.taxiWaitingZeroPointsChart = new Chart(
        this.taxiWaitingZeroPointsCanvasTarget,
        {
          type: "line",
          data: data.taxi_waiting_zero_points_series,
          options: {
            responsive: true,
            plugins: {
              title: {
                display: true,
                text: "1時間あたりの平均待ち台数（箇所）"
              },
              legend: {
                display: true,
                position: "top"
              }
            },
            scales: {
              x: {
                title: { display: true, text: "時間帯" }
              },
              y: {
                title: { display: true, text: "発生ポイント数（箇所）" },
                beginAtZero: true
              }
            }
          }
        }
      );
    }
  }

  update(event) {
    const form = event.target;
    if (event.submitter?.value === "csv") {
      return;
    }

    event.preventDefault();
    let url =
      form.action + "?" + new URLSearchParams(new FormData(form)).toString();

    if (!url.includes("format=json")) url += "&format=json";

    this.renderChart(url);
  }
}
